import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import { useUpdatePlannedOrder, type PlannedOrder } from "@/api/planningRun";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";

/** Cancelling a new order sets a time fence; cancelling an open order plans
 *  without it. */
export function CancelOrderDialog({
  order,
  onClose,
}: {
  order: PlannedOrder;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const update = useUpdatePlannedOrder();
  const openOrder = order.sourceExecution != null;
  const schema = z.object({
    timeFence: openOrder
      ? z.null()
      : z.string().refine((date) => date >= order.dueDate, {
          message: t(
            "planningReview.timeFenceBeforeDue",
            "Choose a date on or after the arrival.",
          ),
        }),
  });
  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      timeFence: openOrder
        ? null
        : (order.timeFence ?? order.suggestedTimeFence),
    },
  });
  const timeFence = form.watch("timeFence");
  const submit = form.handleSubmit(({ timeFence }) =>
    update.mutate(
      { run: order.run, id: order.id, status: "cancelled", timeFence },
      { onSuccess: onClose },
    ),
  );

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {openOrder
              ? t("planningReview.cancelOpenOrder", "Cancel order")
              : t("planningReview.skipReplenishment", "Skip replenishment")}
          </DialogTitle>
          <DialogDescription>
            {order.itemSku} · {order.itemName} · {order.locationName}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form id="cancel-order" onSubmit={submit}>
            {!openOrder && (
              <FormField
                control={form.control}
                name="timeFence"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t(
                        "planningReview.skipThrough",
                        "Skip through (inclusive)",
                      )}
                    </FormLabel>
                    <FormControl>
                      <Input type="date" {...field} value={field.value ?? ""} />
                    </FormControl>
                    <FormMessage />
                    {timeFence != null &&
                      timeFence > order.suggestedTimeFence && (
                        <FormDescription>
                          {t(
                            "planningReview.skipDropsLaterOrders",
                            "Planned orders due through this date are dropped too. Firm the ones you want to keep.",
                          )}
                        </FormDescription>
                      )}
                  </FormItem>
                )}
              />
            )}
          </form>
        </Form>
        {update.isError && (
          <p role="alert" className="text-destructive text-sm">
            {t(
              "planningReview.saveFailed",
              "Could not save this order. Reload the plan and try again.",
            )}
          </p>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t("common.buttons.cancel", "Cancel")}
          </Button>
          <Button type="submit" form="cancel-order" disabled={update.isPending}>
            {t("planningReview.saveDecision", "Save decision")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
